import './styles/Watch.scss';

import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { cn, hasRichTextContent, tryCatch } from '../../../../helpers';
import { parseError } from '../../../../helpers/errorUtils';
import { useClassHelper, useGoogleFonts } from '../../../../hooks';
import { WebsheetTemplateProps } from '../../../../types';
import AppleLoading from '../../../AppleLoading';
import { Dropdown } from '../../../Dropdown/Dropdown';
import { Form } from '../../../Form';
import { RadioCard } from '../../../RadioCard';
import { Stack } from '../../../Stack';
import { TextEditor } from '../../../TextEditor';
import { UnexpectedErrorWebsheet } from '../../../UnexpectedErrorWebsheet/UnexpectedErrorWebsheet';
import { Button } from '../../components/Button';
import { DefaultErrorTemplate } from '../../components/DefaultErrorTemplate';
import { FeedbackSection } from '../../components/FeedbackSection';
import { ImageHeader } from '../../components/ImageHeader';
import { DATADOG_ACTIONS } from '../../constants';
import { renderParagraphOrRichText } from '../../helpers';
import { trackSdkCall } from '../../helpers/trackSdkCall';
import { useDataDogActions } from '../../hooks/useDataDogActions';
import { useGetStyles } from '../../hooks/useGetStyles';
import { useLocalisation } from '../../hooks/useLocalisation';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import {
  componentName,
  NOTIFICATION_KEY_MAP,
  userContractFields,
  userFeedbackFields,
  userFeedbackSuccess,
  watchErrorFields,
  watchErrorFieldsMessages,
  watchFamilyPlanFields,
  watchNotificationFields,
} from './constants';
import { ContractSummaryW } from './ContractSummary_W';
import {
  findPlanDetails,
  resolveContractContent,
  resolvePlanSelectionText,
} from './helpers';
import { useCarrierPlans, useWatchImage } from './hooks';
import { TermsConditionsW } from './TermsConditions_W';

const DATA_DOG_CONTEXT = 'watch_activation';

type WatchView =
  | { type: 'form' }
  | { type: 'terms' }
  | { type: 'contract' }
  | { type: 'success' }
  | {
      type: 'error';
      errorCode: string;
      errorMessage: string;
      // From the 400 body — picks the paired vs Family Plan error copy.
      subscriptionType?: string;
    };

const WatchA = (props: WebsheetTemplateProps) => {
  const { t } = useTranslation();
  const {
    values,
    text, // currentText from PreviewContainer.tsx
    deviceSdk,
    currentLocale,
    services,
    state,
    portalMessenger,
    isPortalPreview,
    selectedField,
    deviceType,
  } = props;

  const { actions, dataDogLogger, isPortalTest } = useDataDogActions({
    isPortalPreview,
    actionKey: DATA_DOG_CONTEXT,
  });

  const { localisations } = values;

  // Always call the hook (Rules of Hooks); pass the logger only outside preview
  // so the localisation_not_found warning never fires in preview/test mode.
  const localisedWatch = useLocalisation(
    localisations,
    currentLocale,
    isPortalPreview ? undefined : dataDogLogger,
    'watch',
  );

  const currentLocalisation = isPortalPreview ? text : localisedWatch;

  const isPreviewLayout = isPortalPreview || isPortalTest;

  useGoogleFonts([values.fontParagraph, values.fontTitle]);

  const c = useClassHelper(componentName);

  const [view, setView] = useState<WatchView>({ type: 'form' });

  const { data, status, error } = useCarrierPlans({ services, state });

  // Fallback rule: a missing attribute defaults to 'paired' so the standard
  // experience is preserved for backward compatibility.
  const subscriptionType = data?.subscription_type ?? 'paired';
  const isLinked = subscriptionType === 'linked';

  // A failed plan load is a fact of the query, not a navigation, so its error
  // view is derived here instead of being pushed into `view` by an effect.
  // The query never retries or refetches, so once present it is terminal.
  const loadErrorView = useMemo<WatchView | undefined>(() => {
    if (!error) {
      return undefined;
    }

    const {
      errorCode,
      errorMessage,
      subscriptionType: errorSubscriptionType,
    } = parseError(error);

    return {
      type: 'error',
      errorCode: errorCode ?? 'UNKNOWN',
      errorMessage,
      subscriptionType: errorSubscriptionType ?? subscriptionType,
    };
  }, [error, subscriptionType]);

  const currentView = loadErrorView ?? view;

  // In portal preview there is no `subscription_type`, so the carrier previews the
  // Family Plan (linked) variant by selecting one of its localisation fields.
  const isFamilyPlanSelected = watchFamilyPlanFields.includes(
    selectedField?.field as (typeof watchFamilyPlanFields)[number],
  );
  const isLinkedView = isLinked || (isPortalPreview && isFamilyPlanSelected);

  // The contract page exists only when the carrier wrote a body for it. The
  // wizard saves every contract key, blank included, so key presence says
  // nothing; in linked mode the twin falls back to the base copy the same
  // way ContractSummaryW renders it.
  const hasUserContractPage = hasRichTextContent(
    resolveContractContent(isLinkedView, currentLocalisation),
  );

  // In linked mode prefer the Family Plan watch background, falling back to the
  // paired background so brands that don't set a Family Plan colour are unchanged.
  const styleValues = useMemo(() => {
    const familyPlanBackground =
      values.color_style_config?.watch_background_family_plan;

    if (!isLinkedView || !familyPlanBackground) {
      return values;
    }

    return {
      ...values,
      color_style_config: {
        ...values.color_style_config,
        watch_background: familyPlanBackground,
      },
    };
  }, [values, isLinkedView]);

  const getStyles = useGetStyles({ values: styleValues });

  const isNotificationSelected = watchNotificationFields.includes(
    selectedField?.field as (typeof watchNotificationFields)[number],
  );

  // In portal preview the carrier selects a notification field (base or
  // family_plan_ twin) to preview its message; at runtime the banner is driven
  // by the `notification` attribute, preferring the Family Plan twin of the
  // mapped key in linked mode.
  const notificationKey =
    isPortalPreview && isNotificationSelected
      ? selectedField?.field
      : data?.notification
      ? NOTIFICATION_KEY_MAP[data.notification]
      : undefined;

  const notificationText = text as Record<string, unknown> | undefined;
  const notificationMessage = notificationKey
    ? (isLinked
        ? notificationText?.[`family_plan_${notificationKey}`]
        : undefined) ?? notificationText?.[notificationKey]
    : undefined;

  // Tag the RUM session with the subscription mode so every activation event
  // (success, error, timings) is segmentable by paired vs linked. Runtime only.
  useEffect(() => {
    if (isPreviewLayout || !data) {
      return;
    }
    dataDogLogger.addGlobal('subscription_type', subscriptionType);
  }, [data, dataDogLogger, isPreviewLayout, subscriptionType]);

  // Emit an event when the cross-plan notification banner is shown at runtime
  // (driven by the `notification` attribute; nothing emits in preview/test).
  useEffect(() => {
    if (isPreviewLayout || !data?.notification) {
      return;
    }
    dataDogLogger.log(actions?.notification_shown, {
      notification: data.notification,
      subscription_type: subscriptionType,
    });
    // Tag the session so the subsequent submit events record that the user
    // proceeded with an active complementary plan (Option B "on top").
    dataDogLogger.addGlobal('cross_plan_notification', data.notification);
  }, [
    actions?.notification_shown,
    data?.notification,
    dataDogLogger,
    isPreviewLayout,
    subscriptionType,
  ]);

  // In 'linked' (sa-watch / Family Plan) mode the plan-selection section is driven
  // by the carrier's Family Plan localisation fields, falling back to the standard
  // plan-selection fields when a Family Plan field is not configured.
  const {
    title: planTitle,
    content: planContent,
    continue: planContinue,
    checkboxLabel: planCheckbox,
    link: planLink,
  } = resolvePlanSelectionText(
    isLinkedView,
    text as Record<string, unknown> | undefined,
  );

  const isFeedbackBySelectedField = userFeedbackFields.includes(
    selectedField?.field as (typeof userFeedbackFields)[number],
  );

  const isSuccessFieldsSelected = userFeedbackSuccess.includes(
    selectedField?.field as (typeof userFeedbackSuccess)[number],
  );

  // Activation feedback copy (submitting/success/close) per plan side: linked
  // mode prefers the `family_plan_` twin, falling back to the paired key.
  const pickFeedbackText = (key: string): string => {
    return ((isLinkedView ? text?.[`family_plan_${key}`] : '') ||
      text?.[key] ||
      '') as string;
  };

  const isSelectedFieldContract = userContractFields.includes(
    selectedField?.field as (typeof userContractFields)[number],
  );

  const isErrorFieldsSelected = watchErrorFields.includes(
    selectedField?.field as (typeof watchErrorFields)[number],
  );

  const formik = useFormik({
    initialValues: {
      plan_offer_id: undefined,
    },
    onSubmit: async values => {
      // Mark the activation start so DataDog can derive submit→success latency
      // from the view-relative custom timings.
      dataDogLogger.addTiming(DATADOG_ACTIONS.timings.activation_submitted);

      // tryCatch wraps the entire activation flow — any thrown error lands in `error`
      // without crashing the component, allowing graceful error handling below.
      const { error, data } = await tryCatch(async () => {
        const payload = {
          state,
          plan_offer_id: values.plan_offer_id,
        };

        const activationData = await services.watchActivation(payload);

        // error_code 204 means the SIM is in pending release —
        // the device SDK handles this as a separate flow
        const pendingReleaseFlow = activationData?.error_code === 204;

        await deviceSdk.watchActivation(activationData, pendingReleaseFlow);

        if (portalMessenger) {
          portalMessenger.propagateSuccess();
        }

        return {
          success: true,
          ...activationData,
        };
      });

      // --- Error path ---
      if (error) {
        const {
          errorCode,
          errorMessage,
          statusCode,
          subscriptionType: errorSubscriptionType,
        } = parseError(error);

        if (portalMessenger) {
          portalMessenger.propagateError();
        }

        // Skip DataDog in preview/test mode to avoid polluting production metrics
        if (!isPortalPreview) {
          dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
            flow: 'submit',
            statusCode,
            errorCode: errorCode ?? 'UNKNOWN',
            errorMessage,
          });
        }

        setView({
          type: 'error',
          errorCode: errorCode ?? 'UNKNOWN',
          errorMessage,
          // Body value wins; the flow's own subscription type covers codes
          // that omit it (e.g. SESSION_NOT_FOUND until UES-12175 lands).
          subscriptionType: errorSubscriptionType ?? subscriptionType,
        });
        return;
      }

      // --- Success path ---
      if (data?.success && !isPortalPreview) {
        dataDogLogger.log(actions?.submit_success, {
          flow: 'submit',
        });

        // Mark activation completion → (succeeded − submitted) = activation latency.
        dataDogLogger.addTiming(DATADOG_ACTIONS.timings.activation_succeeded);

        // Tag the RUM session as completed and emit the success marker while the
        // session is still live. stopSession is deferred to the success-screen
        // close so the feedback interactions stay in the same session.
        dataDogLogger.addGlobal('completed', true);
        dataDogLogger.log(DATADOG_ACTIONS.success.template_success, {
          flow: 'submit',
        });
      }

      setView({ type: 'success' });
    },
    validationSchema: Yup.object({
      plan_offer_id: Yup.string().required('Required.'),
    }),
  });

  const {
    setFieldValue,
    values: formikValues,
    isSubmitting,
    handleSubmit,
    handleChange,
    errors,
    submitForm,
  } = formik;

  // Wait until data is available to set the initial value
  useEffect(() => {
    if (data) {
      const isAutoSelect = (data.plans?.length ?? 0) === 1;
      const planOfferId = isAutoSelect ? data.plans[0].id : undefined;
      setFieldValue('plan_offer_id', planOfferId);
    }
  }, [data, setFieldValue]);

  // Derived boolean keeps the useEffect dep stable — avoids re-firing on
  // every view object re-creation while preserving the same trigger semantics.
  const isSuccess = currentView.type === 'success';

  // Tracks when the user actually sees the success screen, separate from
  // `submit_success` which fires in onSubmit when the API returns. A render
  // bug could set view to 'success' without FeedbackSection ever mounting —
  // this catches that gap.
  useEffect(() => {
    if (isSuccess && !isPortalPreview) {
      dataDogLogger.log(actions?.success_screen_shown, {
        flow: 'submit',
      });
    }
  }, [
    actions?.success_screen_shown,
    dataDogLogger,
    isPortalPreview,
    isSuccess,
  ]);

  // Skip DataDog tracking in preview/test mode to avoid polluting production metrics
  useEffect(() => {
    if (!error || isPortalPreview) {
      return;
    }

    const { errorCode, errorMessage, statusCode } = parseError(error);

    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'load_plans',
      statusCode,
      errorCode: errorCode ?? 'UNKNOWN',
      errorMessage,
    });
  }, [dataDogLogger, error, isPortalPreview]);

  const isSubmitDisabled = useMemo(() => {
    const hasErrors = Boolean(errors.plan_offer_id);
    const isPlanSelected = Boolean(formikValues.plan_offer_id);

    return hasErrors || !isPlanSelected;
  }, [formikValues, errors]);

  const handleOpenTermsAndConditions = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();

    if (isPortalPreview) {
      return;
    }

    setView({ type: 'terms' });
  };

  const { headerImageUrl, imageConfig } = useWatchImage(
    values,
    deviceType,
    isLinkedView,
  );

  // Ref on the main content render. checkPresence is off so the other view
  // branches (terms/contract/error/feedback) — where this ref is null — stay
  // no-ops instead of firing a false MISSING_DOM.
  const containerRef = useRef<HTMLElement>(null);

  useVisibilityCheck(containerRef, {
    enabled: !isPortalPreview,
    checkPresence: false,
    logger: dataDogLogger,
    errorAction: DATADOG_ACTIONS.errors.template_error,
    flow: 'render',
  });

  const handleImageError = (imageUrl: string) => {
    if (isPortalPreview) {
      return;
    }

    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'render_logo',
      statusCode: 0,
      errorCode: 'IMAGE_LOAD_FAILED',
      errorMessage: `Logo image failed to load: ${imageUrl}`,
      imageUrl,
    });
  };

  if (status === 'pending') {
    return (
      <div
        className={c('__loading-wrapper')}
        role="status"
        aria-busy="true"
        aria-live="polite"
      >
        <AppleLoading />
      </div>
    );
  }

  // Contract Summary section
  if (currentView.type === 'contract' || isSelectedFieldContract) {
    return (
      <ContractSummaryW
        {...props}
        isLinked={isLinkedView}
        data-dd-action-name-close={actions?.contract_summary_closed}
        data-dd-action-name-submit={actions?.contract_summary_submitted}
        onClose={() => {
          if (isPortalPreview) {
            return;
          }

          setView({ type: 'form' });
        }}
        onSubmit={async () => {
          if (isPortalPreview) {
            return;
          }

          setView({ type: 'form' });
          await submitForm();
        }}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Terms and conditions section
  if (currentView.type === 'terms') {
    return (
      <TermsConditionsW
        {...props}
        onClose={() => {
          setView({ type: 'form' });
        }}
        isPortalPreview={isPortalPreview}
        isLinked={isLinkedView}
        warningLogger={!isPortalPreview ? dataDogLogger : undefined}
        data-dd-action-name={actions?.close_terms_and_conditions}
      />
    );
  }

  if (currentView.type === 'error' || isErrorFieldsSelected) {
    const isErrorMessageSelected = watchErrorFieldsMessages.includes(
      selectedField?.field,
    );

    const errorCode =
      isPortalPreview && selectedField?.field && isErrorMessageSelected
        ? selectedField?.field
        : currentView.type === 'error'
        ? currentView.errorCode
        : '';

    // Runtime: the error's own subscription_type decides the copy side.
    // Preview: selecting any family_plan_ field flips to the linked variant.
    const isLinkedError =
      currentView.type === 'error' && !isPortalPreview
        ? (currentView.subscriptionType ?? subscriptionType) === 'linked'
        : isLinkedView;

    const errorTitle =
      (isLinkedError ? (text?.family_plan_error_title as string) : '') ||
      text?.error_title ||
      '';

    const errorCloseButton =
      (isLinkedError ? (text?.family_plan_close_button as string) : '') ||
      text?.close_button;

    if (errorCode === 'INVALID_STATE') {
      return (
        <UnexpectedErrorWebsheet
          title={t('error.title')}
          body={t('error.body')}
          close={t('error.close')}
          onClick={() => {
            if (deviceSdk && deviceSdk.exit) {
              // exit() emits finishFlow + dismissFlow actions; stop the session
              // after so those logs are captured in it.
              trackSdkCall('exit', () => deviceSdk.exit(), dataDogLogger);
              dataDogLogger.stopSession();
              return;
            }

            // SDK method missing — the user's close does nothing. Report the
            // silent failure, then close the session.
            if (!isPortalPreview) {
              dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
                flow: 'sdk_unavailable',
                errorCode: 'SDK_METHOD_MISSING',
                errorMessage: 'deviceSdk.exit is unavailable',
                method: 'exit',
              });
              dataDogLogger.stopSession();
            }
          }}
        />
      );
    }

    return (
      <DefaultErrorTemplate
        title={errorTitle}
        closeButtonText={errorCloseButton}
        baseComponentName={componentName}
        style={getStyles as Record<string, string | number>}
        errorCode={errorCode}
        isPortalPreview={isPortalPreview}
        isLinked={isLinkedError}
        localisation={text}
        onClick={async () => {
          if (isPortalPreview) {
            return;
          }

          if (portalMessenger) {
            portalMessenger.propagateError();
          }

          try {
            await deviceSdk.decline();
          } catch (declineError) {
            const { errorCode, errorMessage, statusCode } =
              parseError(declineError);

            dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
              flow: 'decline',
              statusCode,
              errorCode: errorCode ?? 'UNKNOWN',
              errorMessage,
            });
          } finally {
            dataDogLogger.stopSession();
          }
        }}
        data-dd-action-name={actions?.error_message_closed}
      />
    );
  }

  if (status === 'success') {
    if (isSubmitting || isSuccess || isFeedbackBySelectedField) {
      // Preview: success-state fields (incl. close_button) show the success
      // screen, submitting fields show the spinner — same split as
      // WatchTransfer. Runtime keeps the view-driven flag.
      const showSuccess = isFeedbackBySelectedField
        ? isSuccessFieldsSelected
        : isSuccess;

      return (
        <FeedbackSection
          componentName={componentName}
          style={getStyles}
          isSubmitting={!showSuccess}
          onClick={() => {
            if (isPortalPreview) {
              return;
            }

            // Let close() emit its own finishFlow action first, then stop the
            // session so that log lands in it. The native teardown is posted
            // async, so this synchronous tick completes before the webview dies.
            trackSdkCall('close', () => deviceSdk.close(), dataDogLogger);
            dataDogLogger.stopSession();
          }}
          text={{
            submitting_title:
              pickFeedbackText('watch_activation_submitting_title') ||
              'Activating Watch Sidekick Plan',
            success_title:
              pickFeedbackText('watch_activation_success_title') ||
              'Watch Sidekick Plan Activated',
            close_button: pickFeedbackText('close_button') || 'Close',
            submitting_paragraph:
              pickFeedbackText('watch_activation_submitting_paragraph') ||
              'Activating your plan...',
            success_paragraph:
              pickFeedbackText('watch_activation_success_paragraph') ||
              'Plan activated successfully. Your watch is now ready to use.',
          }}
          selectedField={selectedField}
          isPortalPreview={isPortalPreview}
          data-dd-action-name={actions?.feedback_section_closed}
        />
      );
    }

    return (
      <main ref={containerRef} className={c('base')} style={getStyles}>
        <Stack
          direction="column"
          className={c('__inner', { '__inner-preview': isPreviewLayout })}
        >
          {Boolean(headerImageUrl) && (
            <ImageHeader
              url={headerImageUrl as string}
              alt="watch"
              isPortalPreview={isPreviewLayout}
              imageConfig={imageConfig}
              onError={handleImageError}
            />
          )}

          <section
            className={cn('flex-1 shrink basis-0 pt-5 pb-6 px-4', {
              'pb-9': isPortalPreview,
            })}
          >
            <Form
              loading={isSubmitting}
              onSubmit={event => {
                event.preventDefault();

                if (isPortalPreview) {
                  return;
                }

                // Submit already in flight — ignore re-taps without a silent
                // no-op so they don't pile up as RUM rage clicks.
                if (isSubmitting) {
                  return;
                }

                if (hasUserContractPage) {
                  // Track in DataDog RUM as custom action
                  dataDogLogger.log(actions?.contract_summary_opened, {
                    flow: 'contract',
                  });

                  setView({ type: 'contract' });
                  return;
                }

                handleSubmit(event);
              }}
              className={c('__inner-form')}
            >
              <Stack direction="column" gap={4} justify="between" height="full">
                <Stack
                  direction="column"
                  className={cn('gap-4', {
                    'gap-[13px]!': isPreviewLayout,
                    [c('__plan-selection--linked')]: isLinkedView,
                  })}
                >
                  {notificationMessage && (
                    <div className={c('__notification')}>
                      {renderParagraphOrRichText(
                        notificationMessage as never,
                        values?.fontTitle,
                      )}
                    </div>
                  )}

                  <h1
                    className={c('__title', {
                      '__title--spacing': !headerImageUrl,
                      '__title--is-preview': isPreviewLayout,
                    })}
                  >
                    {planTitle && planTitle.length > 0
                      ? planTitle
                      : 'Select your Watch Plan'}
                  </h1>

                  <Stack direction="column" gap={4}>
                    {data?.plans?.map((plan, i) => {
                      const { name, description, description_extended } =
                        findPlanDetails(plan, currentLocale);

                      if (name) {
                        return (
                          <RadioCard
                            key={i}
                            name="plan_offer_id"
                            title={name}
                            value={plan.id}
                            description={description}
                            descriptionExtended={description_extended}
                            checked={formikValues.plan_offer_id === plan.id}
                            onChange={event => {
                              if (isPortalPreview) {
                                return;
                              }

                              handleChange(event);
                            }}
                            isPortalPreview={isPreviewLayout}
                          />
                        );
                      }

                      return null;
                    })}
                  </Stack>

                  {planContent && (
                    <div
                      className={c('__content', {
                        '__content-preview': isPreviewLayout,
                      })}
                    >
                      <TextEditor
                        viewOnly
                        value={planContent as never}
                        valueFontParagraph={values?.fontParagraph}
                      />
                    </div>
                  )}
                </Stack>

                <Stack
                  direction="column"
                  gap={isPreviewLayout ? 2 : 4}
                  justify="center"
                >
                  <Button
                    type="submit"
                    aria-disabled={isSubmitDisabled || isSubmitting}
                    data-dd-action-name={actions?.submit_button_clicked}
                  >
                    {planContinue || 'Continue'}
                  </Button>
                  {data?.requires_tc && (
                    <div
                      className={c('__terms', {
                        '__terms-preview': isPreviewLayout,
                      })}
                    >
                      <Dropdown.GoogleFont.Viewer
                        fontFamily={values?.fontParagraph}
                      >
                        {planCheckbox ||
                          'By continuing you agree to accept the '}{' '}
                        <a
                          href="/terms-and-conditions"
                          onClick={handleOpenTermsAndConditions}
                          className={c('__link')}
                          data-dd-action-name={
                            actions?.open_terms_and_conditions_clicked
                          }
                        >
                          {planLink || 'Terms & Conditions'}
                        </a>
                      </Dropdown.GoogleFont.Viewer>
                    </div>
                  )}
                </Stack>
              </Stack>
            </Form>
          </section>
        </Stack>
      </main>
    );
  }

  return null;
};

export default WatchA;
